/**
 * GraphQL Mood Service — backend persistence for MoodMate.
 *
 * Caller-scoped by design: NO operation accepts a userId. The AppSync Lambda
 * resolver derives the authenticated user's email from their Cognito token and
 * uses it as the owner, so a client cannot read or write another person's rows,
 * and identity is never trusted from the client. The Lambda also merges a user's
 * own records across the creyentetech→creyenteinfotech domain move server-side,
 * so no client-side identity widening is needed here.
 *
 * There is no codegen in this repo; these operation strings must stay in step
 * with `terraform/modules/appsync/schema.graphql` by hand.
 *
 * Privacy: nothing here logs mood content, journal text, tags or notes — logs
 * carry operation names and counts only, and nothing goes in a URL.
 */

import { generateClient } from 'aws-amplify/api';
import type { MoodCheckInRecord, MoodReflection, MoodTag } from '../contexts/MoodContext';
import type { MoodId } from '../components/moodmate/moods';

let client: ReturnType<typeof generateClient> | null = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const PAGE_LIMIT = 400;
const MAX_ROWS = 3000;

// ── Operation strings (keep aligned with schema.graphql) ─────────────────────
const CHECKIN_FIELDS = `
  userId
  checkinId
  mood
  createdAt
  updatedAt
  journalEntry
  tags
  energyLevel
  notes
`;

const REFLECTION_FIELDS = `
  userId
  reflectionId
  period
  periodKey
  prompt
  text
  createdAt
  updatedAt
`;

// fromDate/toDate are declared String to match the AppSync schema's argument
// types (the stored createdAt is AWSDateTime; the range args are plain Strings).
const MY_MOOD_CHECKINS = `
  query MyMoodCheckins($fromDate: String, $toDate: String, $limit: Int, $nextToken: String) {
    myMoodCheckins(fromDate: $fromDate, toDate: $toDate, limit: $limit, nextToken: $nextToken) {
      items {${CHECKIN_FIELDS}}
      nextToken
    }
  }
`;

const MY_MOOD_REFLECTIONS = `
  query MyMoodReflections($limit: Int, $nextToken: String) {
    myMoodReflections(limit: $limit, nextToken: $nextToken) {
      items {${REFLECTION_FIELDS}}
      nextToken
    }
  }
`;

const CREATE_MOOD_CHECKIN = `
  mutation CreateMoodCheckin($input: CreateMoodCheckinInput!) {
    createMoodCheckin(input: $input) {${CHECKIN_FIELDS}}
  }
`;

const UPDATE_MOOD_CHECKIN = `
  mutation UpdateMoodCheckin($input: UpdateMoodCheckinInput!) {
    updateMoodCheckin(input: $input) {${CHECKIN_FIELDS}}
  }
`;

const DELETE_MOOD_CHECKIN = `
  mutation DeleteMoodCheckin($checkinId: String!) {
    deleteMoodCheckin(checkinId: $checkinId) {
      userId
      checkinId
    }
  }
`;

const CREATE_MOOD_REFLECTION = `
  mutation CreateMoodReflection($input: CreateMoodReflectionInput!) {
    createMoodReflection(input: $input) {${REFLECTION_FIELDS}}
  }
`;

const UPDATE_MOOD_REFLECTION = `
  mutation UpdateMoodReflection($input: UpdateMoodReflectionInput!) {
    updateMoodReflection(input: $input) {${REFLECTION_FIELDS}}
  }
`;

const DELETE_MOOD_REFLECTION = `
  mutation DeleteMoodReflection($reflectionId: String!) {
    deleteMoodReflection(reflectionId: $reflectionId) {
      userId
      reflectionId
    }
  }
`;

// ── Mapping (DynamoDB row → app record) ──────────────────────────────────────
export const mapToCheckIn = (item: any): MoodCheckInRecord => ({
  id: item.checkinId,
  mood: item.mood as MoodId,
  createdAt: item.createdAt,
  ...(item.journalEntry ? { journalEntry: item.journalEntry } : {}),
  ...(Array.isArray(item.tags) && item.tags.length ? { tags: item.tags as MoodTag[] } : {}),
  ...(item.energyLevel != null ? { energyLevel: item.energyLevel } : {}),
  ...(item.notes ? { notes: item.notes } : {}),
});

const mapToReflection = (item: any): MoodReflection => ({
  id: item.reflectionId,
  period: item.period as 'week' | 'month',
  periodKey: item.periodKey,
  prompt: item.prompt,
  text: item.text,
  updatedAt: item.updatedAt || item.createdAt,
});

/** Create/update input from a record, omitting empty optionals. No userId — the Lambda owns it. */
const checkInInput = (record: MoodCheckInRecord) => ({
  checkinId: record.id,
  mood: record.mood,
  createdAt: record.createdAt,
  ...(record.journalEntry ? { journalEntry: record.journalEntry } : {}),
  ...(record.tags?.length ? { tags: record.tags } : {}),
  ...(record.energyLevel != null ? { energyLevel: record.energyLevel } : {}),
  ...(record.notes ? { notes: record.notes } : {}),
});

function hasErrors(response: any): boolean {
  return Array.isArray(response?.errors) && response.errors.length > 0;
}

export class GraphQLMoodService {
  /**
   * The caller's own check-ins (newest first). Paginates to MAX_ROWS so a long
   * history still bounds memory. An optional [from,to] narrows the query. A
   * network failure rejects so the caller can fall back to its offline cache.
   */
  static async listCheckIns(range?: { fromDate?: string; toDate?: string }): Promise<MoodCheckInRecord[]> {
    const items: any[] = [];
    let nextToken: string | null = null;
    do {
      const response: any = await getClient().graphql({
        query: MY_MOOD_CHECKINS,
        variables: { fromDate: range?.fromDate, toDate: range?.toDate, limit: PAGE_LIMIT, nextToken },
      });
      if (hasErrors(response)) {
        console.warn('[GraphQLMoodService] errors in myMoodCheckins');
        break;
      }
      const page = response.data?.myMoodCheckins;
      items.push(...(page?.items || []));
      nextToken = page?.nextToken || null;
    } while (nextToken && items.length < MAX_ROWS);

    return items
      .map(mapToCheckIn)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
      .slice(0, MAX_ROWS);
  }

  static async listReflections(): Promise<MoodReflection[]> {
    const items: any[] = [];
    let nextToken: string | null = null;
    do {
      const response: any = await getClient().graphql({
        query: MY_MOOD_REFLECTIONS,
        variables: { limit: PAGE_LIMIT, nextToken },
      });
      if (hasErrors(response)) {
        console.warn('[GraphQLMoodService] errors in myMoodReflections');
        break;
      }
      const page = response.data?.myMoodReflections;
      items.push(...(page?.items || []));
      nextToken = page?.nextToken || null;
    } while (nextToken && items.length < MAX_ROWS);
    return items.map(mapToReflection);
  }

  /**
   * Create one check-in. The record carries a client id and ISO createdAt; the
   * Lambda keys on the caller's derived email + that id with a conditional put,
   * so re-sending the same record (import, retry) is idempotent — no duplicates.
   */
  static async createCheckIn(record: MoodCheckInRecord): Promise<MoodCheckInRecord | null> {
    const response: any = await getClient().graphql({
      query: CREATE_MOOD_CHECKIN,
      variables: { input: checkInInput(record) },
    });
    if (hasErrors(response)) {
      console.warn('[GraphQLMoodService] errors in createMoodCheckin');
      return null;
    }
    return response.data?.createMoodCheckin ? mapToCheckIn(response.data.createMoodCheckin) : null;
  }

  static async updateCheckIn(
    id: string,
    patch: Partial<Omit<MoodCheckInRecord, 'id' | 'createdAt'>>
  ): Promise<void> {
    const response: any = await getClient().graphql({
      query: UPDATE_MOOD_CHECKIN,
      variables: { input: { checkinId: id, ...patch } },
    });
    if (hasErrors(response)) console.warn('[GraphQLMoodService] errors in updateMoodCheckin');
  }

  static async deleteCheckIn(id: string): Promise<void> {
    const response: any = await getClient().graphql({
      query: DELETE_MOOD_CHECKIN,
      variables: { checkinId: id },
    });
    if (hasErrors(response)) console.warn('[GraphQLMoodService] errors in deleteMoodCheckin');
  }

  /** Create or update a reflection. The record carries a stable client id. */
  static async saveReflection(reflection: MoodReflection, isNew: boolean): Promise<MoodReflection | null> {
    const input = {
      reflectionId: reflection.id,
      period: reflection.period,
      periodKey: reflection.periodKey,
      prompt: reflection.prompt,
      text: reflection.text,
    };
    const response: any = await getClient().graphql({
      query: isNew ? CREATE_MOOD_REFLECTION : UPDATE_MOOD_REFLECTION,
      variables: { input },
    });
    if (hasErrors(response)) {
      console.warn('[GraphQLMoodService] errors in saveMoodReflection');
      return null;
    }
    const data = response.data?.createMoodReflection || response.data?.updateMoodReflection;
    return data ? mapToReflection(data) : null;
  }

  static async deleteReflection(id: string): Promise<void> {
    const response: any = await getClient().graphql({
      query: DELETE_MOOD_REFLECTION,
      variables: { reflectionId: id },
    });
    if (hasErrors(response)) console.warn('[GraphQLMoodService] errors in deleteMoodReflection');
  }
}
